/**
 * Redaction and hashing helpers for client error logging.
 *
 * Client errors must never persist anything that identifies a patient. File names in this application
 * regularly contain patient names (`JanDeVries_front.jpg`), and they leak into far more places than the
 * file name field itself:
 *
 * - Firebase Storage error messages embed the full object path
 *   (`Firebase Storage: User does not have permission to access 'cust/order/1_JanDeVries_front.jpg'`)
 * - Download URLs embed the object path URL-encoded
 * - Stack traces can contain either of the above
 *
 * Everything that gets persisted therefore goes through `redactText` first. The console keeps the
 * unredacted values: devtools output is local and ephemeral, and full fidelity is useful while debugging.
 *
 * Runtime errors and crash reports have no file to redact against — nobody handed them a `File` — so
 * they go through `redactSensitiveText`, which works on shape alone. `redactText` runs it too, because
 * an upload error can carry a *different* patient's name than the file it was reported against.
 */

/** Length of the truncated digest used to identify a file without naming it. */
const HASH_LENGTH = 12;

/**
 * Strings shorter than this are not redacted. Replacing very short fragments would mangle unrelated
 * text, and a one or two character file name is not identifying in the first place.
 */
const MIN_REDACTABLE_LENGTH = 3;

/** Maximum characters kept of any redacted string, so a pathological stack cannot bloat the document. */
const MAX_TEXT_LENGTH = 4000;

/**
 * Extensions whose file name is assumed to identify a patient.
 *
 * Deliberately excludes `js`, `css`, `map` and `html`: stack frames name bundles, and replacing those
 * would destroy the only part of a runtime error worth having.
 */
const PATIENT_FILE_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'heic',
    'heif',
    'tif',
    'tiff',
    'bmp',
    'dcm',
    'dicom',
    'pdf',
    'stl',
    'ply',
    'obj',
    'zip',
    'mp4',
    'mov',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'csv',
];

/** Query parameters that carry a credential. A Storage `token` is a capability URL on its own. */
const CREDENTIAL_PARAMETERS = ['token', 'access_token', 'auth', 'key', 'signature', 'x-goog-signature', 'password'];

/** A file name with a patient-bearing extension, in raw or URL-encoded form. */
const PATIENT_FILE_PATTERN = new RegExp(`[^\\s/\\\\"'<>?&=]+\\.(?:${PATIENT_FILE_EXTENSIONS.join('|')})\\b`, 'gi');

const CREDENTIAL_PARAMETER_PATTERN = new RegExp(`([?&](?:${CREDENTIAL_PARAMETERS.join('|')})=)[^&\\s"']+`, 'gi');

/** Anything shaped like a JWT, which is what a leaked ID token in a network error looks like. */
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g;

const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

/**
 * Splits a file name into the part that may identify a patient and the part that is safe to keep.
 *
 * @param fileName - The original file name, for example `JanDeVries_front.jpg`.
 * @returns The basename without extension, and the lower-cased extension without the dot.
 */
export function splitFileName(fileName: string): { basename: string; extension: string } {
    const lastDot = fileName.lastIndexOf('.');
    // A leading dot means a dotfile rather than an extension, hence `<= 0` rather than `< 0`.
    if (lastDot <= 0) {
        return { basename: fileName, extension: '' };
    }
    return { basename: fileName.slice(0, lastDot), extension: fileName.slice(lastDot + 1).toLowerCase() };
}

/**
 * Produces a stable, non-reversible identifier for a file name.
 *
 * Salting with the uid means the same file retried by the same user is correlatable across events,
 * while the digest is not a lookup-table hash of a bare name.
 *
 * @param uid - The Firebase Auth uid of the user that owns the upload.
 * @param basename - The file name without its extension.
 * @returns A truncated hex digest, for example `9f2a1c4b7e03`.
 */
export async function hashFileName(uid: string, basename: string): Promise<string> {
    const input = `${uid}:${basename}`;
    const subtle = globalThis.crypto?.subtle;

    // `crypto.subtle` is only available in secure contexts. Anything else falls back to a
    // non-cryptographic digest, which is still enough to correlate retries of the same file.
    if (subtle) {
        try {
            const digest = await subtle.digest('SHA-256', new TextEncoder().encode(input));
            return [...new Uint8Array(digest)]
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join('')
                .slice(0, HASH_LENGTH);
        } catch {
            // Fall through to the non-cryptographic digest below.
        }
    }

    return fnv1a(input).toString(16).padStart(8, '0').slice(0, HASH_LENGTH);
}

/**
 * Replaces every occurrence of a file name with an opaque token.
 *
 * Handles the encodings the same name appears in across error messages and download URLs, and always
 * replaces the longest variant first so a shorter one cannot partially consume it.
 *
 * @param text - The text to redact, typically an error message, stack or URL.
 * @param basename - The file name without extension, as returned by `splitFileName`.
 * @param hash - The token to substitute, as returned by `hashFileName`.
 * @returns The redacted text, truncated to a sane maximum length.
 */
export function redactText(text: string | undefined, basename: string, hash: string): string | undefined {
    if (!text) {
        return text;
    }

    let redacted = text;
    for (const variant of nameVariants(basename)) {
        redacted = redacted.replace(new RegExp(escapeRegExp(variant), 'gi'), `<file:${hash}>`);
    }

    return redactSensitiveText(redacted);
}

/**
 * Removes identifying and secret-shaped fragments from text, without knowing what to look for.
 *
 * Runtime errors, unhandled rejections and crash reports have no file to redact against, so this
 * recognises the *shape* of what must not be persisted instead: patient-bearing file names anywhere in
 * a path or URL, credentials in query strings, bearer tokens and email addresses.
 *
 * It is deliberately blunt. Losing a file name from a stack trace costs nothing — the hash and
 * extension are recorded separately — while keeping one is a data leak.
 *
 * @param text - The text to redact, typically an error message, stack or URL.
 * @returns The redacted text, truncated to a sane maximum length.
 */
export function redactSensitiveText(text: string | undefined): string | undefined {
    if (!text) {
        return text;
    }

    const redacted = text
        .replace(JWT_PATTERN, '<token>')
        .replace(CREDENTIAL_PARAMETER_PATTERN, '$1<redacted>')
        .replace(PATIENT_FILE_PATTERN, replacePatientFileName)
        .replace(EMAIL_PATTERN, '<email>');

    return redacted.length > MAX_TEXT_LENGTH ? `${redacted.slice(0, MAX_TEXT_LENGTH)}…[truncated]` : redacted;
}

/**
 * Recursively removes `undefined` values from an object.
 *
 * Firestore rejects `undefined` field values outright. Several fields collected for an upload error are
 * only populated on Chromium (`deviceMemory`, `effectiveType`, `rtt`), so without this every write from
 * Safari and Firefox would be rejected and those browsers would silently log nothing at all.
 *
 * Non-plain objects (`Timestamp`, `FieldValue`, `Date`) are returned untouched so Firestore sentinels
 * survive the pass intact.
 *
 * @param value - The value to clean.
 * @returns The same value with all `undefined` entries removed.
 */
export function stripUndefined<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.filter(entry => entry !== undefined).map(entry => stripUndefined(entry)) as T;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    const cleaned: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry === undefined) {
            continue;
        }
        cleaned[key] = stripUndefined(entry);
    }
    return cleaned as T;
}

/**
 * Maps a string onto a stable value in [0, 1).
 *
 * Used for sampling so that a user is consistently inside or outside the sampled cohort. Sampling per
 * event instead would record half of one upload session, which is more confusing than recording none.
 *
 * @param input - The value to bucket, typically a uid.
 * @returns A deterministic number in [0, 1).
 */
export function stableBucket(input: string): number {
    return fnv1a(input) / 0x1_0000_0000;
}

/** FNV-1a, used both as the non-secure-context digest fallback and as the sampling hash. */
function fnv1a(input: string): number {
    let hash = 0x811c9dc5;
    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash >>> 0;
}

/**
 * Replaces the name of a matched file while keeping everything around it.
 *
 * The match can span several path segments, because a URL-encoded Storage path separates them with
 * `%2F` rather than `/`. Only the leaf is a name; the segments before it are the customer and order
 * ids, which are exactly what makes the event traceable.
 */
function replacePatientFileName(match: string): string {
    const leaf = match.split(/%2f/i).pop() ?? match;
    const prefix = match.slice(0, match.length - leaf.length);
    return `${prefix}<file>${leaf.slice(leaf.lastIndexOf('.'))}`;
}

/** The encodings a file name can appear in across error messages, storage paths and download URLs. */
function nameVariants(basename: string): string[] {
    const variants = new Set<string>([basename]);

    try {
        variants.add(encodeURIComponent(basename));
        variants.add(encodeURI(basename));
    } catch {
        // Lone surrogates make encodeURIComponent throw; the raw variant is still redacted.
    }
    variants.add(basename.replace(/ /g, '%20'));
    variants.add(basename.replace(/ /g, '+'));

    // Longest first, so a shorter variant cannot partially consume a longer one.
    return [...variants].filter(variant => variant.length >= MIN_REDACTABLE_LENGTH).sort((a, b) => b.length - a.length);
}

function escapeRegExp(input: string): string {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
